import api from "@/api";
import i18n from "@/i18n";

export default {
    state: {
        leads: [],
        leadsPerPage: {},
        leadCustomers: [],
        leadCustomersPerPage: {},
        leadsLoading: false,
        leadCustomerToEdit: null, 
        leadToEdit : null,
        leadsToTransfer: [],
    },
    getters: {
        leads: (state) => state.leads,
        leadsPerPage: (state) => state.leadsPerPage,
        leadCustomers: (state) => state.leadCustomers,
        leadCustomersPerPage: (state) => state.leadCustomersPerPage,
        leadsLoading: (state) => state.leadsLoading,
        leadCustomerToEdit: (state) => state.leadCustomerToEdit,  
        leadToEdit: (state) => state.leadToEdit, 
        leadsToTransfer: (state) => state.leadsToTransfer,
    },
    mutations: {
        setLeads(state, leads) {
            state.leads = leads;
        },
        setLeadsPerPage(state, leadsPerPage) {
            state.leadsPerPage = leadsPerPage;
        },
        setLeadCustomers(state, leadCustomers) {
            state.leadCustomers = leadCustomers;
        },
        setLeadCustomersPerPage(state, leadCustomersPerPage) {
            state.leadCustomersPerPage = leadCustomersPerPage;
        },
        setLeadsLoading(state, status) {
            state.leadsLoading = status;
        },
        setLeadCustomerToEdit(state, leadCustomer) {
            state.leadCustomerToEdit = leadCustomer;
        }, 
        setLeadToEdit(state, lead) {
            state.leadToEdit = lead;
        },
        setLeadsToTransfer(state, data) {
            state.leadsToTransfer = data;
        },
    },
    actions: {
        showToast({ commit }, { type, message }) {
            commit("setToast", {
                display: true,
                type: type,
                message: message,
            }, { root: true });
        },

        //menu leads page

        fetchLeadCustomers: async ({ commit, dispatch }, page = 1) => {
            commit("setLeadsLoading", true);

            try {
                const response = await api({ requiresAuth: true }).get(`v1/lead-customers?page=${page}`);
                commit("setLeadCustomersPerPage", response.data);
                return response.data;
            } catch (error) {
                dispatch("showToast", {
                    type: "error",
                    message: error.response?.data?.message || i18n.t('leadCustomerModal.leadError')
                });
                throw error;
            } finally {
                commit("setLeadsLoading", false);
            }
        },

        createLeadCustomer: async ({ commit, dispatch }, customer) => {
            commit("setLeadsLoading", true);

            try {
                const response = await api({ requiresAuth: true }).post("v1/lead-customers", customer);
                dispatch("showToast", {
                    type: "success",
                    message: response.data.message || i18n.t('leadCustomerModal.createSuccess')
                });
                await dispatch("fetchLeadCustomers");
                return response;
            } catch (error) {
                dispatch("showToast", {
                    type: "error",
                    message: error.response?.data?.message || i18n.t('leadCustomerModal.createError')
                });
                throw error;
            } finally {
                commit("setLeadsLoading", false);
            }
        },

        deleteLeadCustomer: async ({ commit, dispatch }, customerId) => {
            commit("setLeadsLoading", true);

            try {
                const response = await api({ requiresAuth: true }).delete(`v1/lead-customers/${customerId}`);
                dispatch("showToast", {
                    type: "success",
                    message: response.data.message || i18n.t('leadCustomerModal.deleteSuccess')
                });

                await dispatch("fetchLeadCustomers");
            } catch (error) {
                dispatch("showToast", {
                    type: "error",
                    message: error.response?.data?.message || i18n.t('leadCustomerModal.deleteError')
                });
                throw error;
            } finally {
                commit("setLeadsLoading", false);
            }
        },

        updateLeadCustomer: async ({ commit, dispatch }, { customerId, customerData }) => {
            commit("setLeadsLoading", true);

            try {
                const response = await api({ requiresAuth: true }).patch(`v1/lead-customers/${customerId}`, customerData);
                dispatch("showToast", {
                    type: "success",
                    message: response.data.message || i18n.t('leadCustomerModal.updateSuccess')
                }); 
                await dispatch("fetchLeadCustomers");
            } catch (error) {
                dispatch("showToast", {
                    type: "error",
                    message: error.response?.data?.message || i18n.t('leadCustomerModal.updateError')
                });
                throw error;
            } finally {
                commit("setLeadsLoading", false);
            }
        },


        transferLeadCustomers: async ({ commit, dispatch }, { customerIds, teamId, agentId }) => {
            commit("setLeadsLoading", true);

            try {
                const response = await api({ requiresAuth: true }).patch("v1/leads/bulk-assign", {
                    customer_ids: customerIds,
                    team_id: teamId,
                    agent_id: agentId
                });
                dispatch("showToast", {
                    type: "success",
                    message: response.data.message || i18n.t('leadCustomerModal.transferSuccess')
                });
                await dispatch("fetchLeadCustomers");
            } catch (error) {
                dispatch("showToast", {
                    type: "error",
                    message: error.response?.data?.message || i18n.t('leadCustomerModal.transferError')
                });
                throw error;
            } finally {
                commit("setLeadsLoading", false);
            }
        },


        //inside customer details page

        fetchLeads: async ({ commit, dispatch }, { customerId, page = 1 }) => {
            commit("setLeadsLoading", true);

            try {
                const response = await api({ requiresAuth: true }).get(`v1/customer-leads/${customerId}?page=${page}`);
                commit("setLeadsPerPage", response.data);
                return response.data;
            } catch (error) {
                dispatch("showToast", {
                    type: "error",
                    message: error.response?.data?.message || i18n.t('addLeadModal.fetchError')
                });
                throw error;
            } finally {
                commit("setLeadsLoading", false);
            }
        },

        createLead: async ({ commit, dispatch }, { leadData, files = [] }) => {
            commit("setLeadsLoading", true);

            try {
                const response = await api({ requiresAuth: true }).post(
                    "v1/leads", 
                    leadData
                );
                
                dispatch("showToast", {
                    type: "success",
                    message: response.data.message || i18n.t('addLeadModal.createSuccess')
                });

                const createdLead = response.data.data;

                // Upload files if any
                if (files.length > 0 && createdLead && createdLead.id) {
                    await dispatch("addLeadFiles", {
                        leadId: createdLead.id,
                        files
                    });
                }

                await dispatch("fetchLeads", { customerId: leadData.customer_id });
                return createdLead;
            } catch (error) {
                dispatch("showToast", {
                    type: "error",
                    message: error.response?.data?.message || i18n.t('addLeadModal.createError')
                });
                throw error;
            } finally {
                commit("setLeadsLoading", false);
            }
        },

        deleteLead: async ({ commit, dispatch }, {leadId, customerId}) => {
            commit("setLeadsLoading", true);

            try {
                const response = await api({ requiresAuth: true }).delete(`v1/leads/${leadId}`);
                dispatch("showToast", {
                    type: "success",
                    message: response.data.message || i18n.t('addLeadModal.deleteSuccess')
                });

                await dispatch("fetchLeads", { customerId });
            } catch (error) {
                dispatch("showToast", {
                    type: "error",
                    message: error.response?.data?.message || i18n.t('addLeadModal.deleteError')
                });
                throw error;
            } finally {
                commit("setLeadsLoading", false);
            }
        },

        updateLead: async ({ commit, dispatch }, { leadId, leadData }) => {
            commit("setLeadsLoading", true);

            try {
                const response = await api({ requiresAuth: true }).patch(`v1/leads/${leadId}`, leadData);
                dispatch("showToast", {
                    type: "success",
                    message: response.data.message || i18n.t('addLeadModal.updateSuccess')
                });

                await dispatch("fetchLeads", { customerId: leadData.customer_id });
            } catch (error) {
                dispatch("showToast", {
                    type: "error",
                    message: error.response?.data?.message || i18n.t('addLeadModal.updateError')
                });
                throw error;
            } finally {
                commit("setLeadsLoading", false);
            }
        },

        addLeadFiles: async (_, { leadId, files }) => {
            const formData = new FormData();

            files.forEach((file) => {
                formData.append("lead_files[]", file);
            });
            await api({ requiresAuth: true }).post(
                `v1/leads/${leadId}/files`,
                formData,
                {
                    headers: {
                        "Content-Type": "multipart/form-data",
                    }
                }
            );
        },

        fetchLeadFiles: async ({ commit }, leadId) => {
            const response = await api({ requiresAuth: true }).get(
                `/v1/leads/${leadId}`
            );
            const { data } = response.data;
            commit("setFilesToEdit", data.files);
            return data;
        },
    },
};